import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Popover,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import FilterChip, { FilterChipVariant } from "./FilterChip";
import styles from "./FilterChip.module.css";

export interface FilterOption {
  value: string;
  label: string;
}

export interface MultiSelectFilterMenuProps {
  /** Field name on the chip and in accessible names, e.g. "Staff". */
  label: string;
  options: FilterOption[];
  /** Selected option values. Empty means "no filter" (shows `allLabel`). */
  selected: string[];
  onChange: (values: string[]) => void;
  /** When set, the footer offers "Remove filter" (for optional chips). */
  onRemove?: () => void;
  /** Chip value when nothing is selected. Defaults to "All". */
  allLabel?: string;
  /** Show a search box once there are more options than this. Default 8. */
  searchThreshold?: number;
  /** Open the menu on mount (e.g. right after "+ Filter" adds the chip). */
  defaultOpened?: boolean;
  /** Dropdown width in px. Defaults to DEFAULT_MENU_WIDTH. */
  width?: number;
  /** Chip look; see FilterChip. Defaults to the Sales "token". */
  variant?: FilterChipVariant;
  /** Opt-in: ellipsize the chip value past this width. */
  valueMaxWidth?: number | string;
}

/** Default dropdown width (fits a staff name + checkbox comfortably). */
export const DEFAULT_MENU_WIDTH = 260;
/** The option list scrolls past this height. */
const MENU_LIST_MAX_HEIGHT = 280;

/** "All", the single selected label, or "First +N". */
export function summarizeSelection(
  options: FilterOption[],
  selected: string[],
  allLabel: string,
): string {
  if (selected.length === 0) return allLabel;
  const byValue = new Map(options.map((o) => [o.value, o.label]));
  const first = byValue.get(selected[0]) ?? selected[0];
  return selected.length === 1 ? first : `${first} +${selected.length - 1}`;
}

/**
 * A filter chip that opens a multi-select checkbox menu. A search field
 * appears when the option list is long; the footer clears the selection and,
 * for optional filters, removes the chip entirely.
 */
export default function MultiSelectFilterMenu({
  label,
  options,
  selected,
  onChange,
  onRemove,
  allLabel = "All",
  searchThreshold = 8,
  defaultOpened = false,
  width = DEFAULT_MENU_WIDTH,
  variant = "token",
  valueMaxWidth,
}: MultiSelectFilterMenuProps) {
  const [opened, setOpened] = useState(defaultOpened);
  const [query, setQuery] = useState("");
  const showSearch = options.length > searchThreshold;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const summary = summarizeSelection(options, selected, allLabel);

  const toggle = (value: string) => {
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const close = () => {
    setOpened(false);
    setQuery("");
  };

  return (
    <Popover
      opened={opened}
      onChange={(next) => (next ? setOpened(true) : close())}
      position="bottom-start"
      width={width}
      shadow="md"
      radius="var(--radius-lg)"
      withinPortal
      trapFocus={showSearch}
    >
      <Popover.Target>
        <FilterChip
          variant={variant}
          label={label}
          value={summary}
          valueMaxWidth={valueMaxWidth}
          active={selected.length > 0}
          aria-label={`${label} filter: ${summary}`}
          onClick={() => (opened ? close() : setOpened(true))}
        />
      </Popover.Target>
      <Popover.Dropdown p="xs">
        {showSearch && (
          <Box pb="xs">
            <TextInput
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder={`Search ${label.toLowerCase()}`}
              aria-label={`Search ${label} options`}
              leftSection={<IconSearch size={14} aria-hidden />}
            />
          </Box>
        )}
        <ScrollArea.Autosize mah={MENU_LIST_MAX_HEIGHT} type="auto">
          <Box className={styles.menuList} role="group" aria-label={label}>
            {visible.map((o) => (
              <Checkbox
                key={o.value}
                className={styles.option}
                size="xs"
                label={o.label}
                checked={selectedSet.has(o.value)}
                onChange={() => toggle(o.value)}
              />
            ))}
            {visible.length === 0 && (
              <Text className={styles.empty}>
                {options.length === 0
                  ? "No options in this range"
                  : "No matches"}
              </Text>
            )}
          </Box>
        </ScrollArea.Autosize>
        <Box className={styles.footer}>
          <UnstyledButton
            type="button"
            className={styles.footerAction}
            disabled={selected.length === 0}
            onClick={() => onChange([])}
          >
            Clear
          </UnstyledButton>
          {onRemove && (
            <UnstyledButton
              type="button"
              className={`${styles.footerAction} ${styles.footerDanger}`}
              onClick={() => {
                close();
                onRemove();
              }}
            >
              Remove filter
            </UnstyledButton>
          )}
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
}
